/**
 * One inbound candidate question.
 *
 * Visually this follows FeedbackCard so the two tabs read as one board: white card on Cotton,
 * `rounded-xl … ring-1 ring-gray-200`, a header row closed by a hairline divider, and a fixed
 * w-48 action column behind a vertical divider. Expanded, the actions move to a bottom bar (the
 * shape SchedulingCard already uses) so the reply editor gets the full width.
 *
 * Only the expanded card renders an EmailBodyEditor — that component uses a fixed DOM id, so more
 * than one mounted at a time would collide.
 */
import { type KeyboardEvent, type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { cn } from '../../../../shared';
import { ClearActions } from './ClearActions';
import { CardBadges, acknowledgesOnly } from './CardBadges';
import type { GradedSlot, InboundMessage, InboundRequest } from '../../types/taActionBoard';
import { ConnectionSlot, originalPromiseOf, proposalIn, proposeInReply, syncProposalInReply, type ReplyTools } from './replyProposal';
import { ProposalBox, RoleStatusLine } from './ProposalBox';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import { ThreadHistory } from './ThreadHistory';
import { EmailBodyEditor } from './EmailBodyEditor';
import { DecisionFeedbackControl } from './DecisionFeedbackControl';
import { draftHtmlWithMarkedFeedback, stripFeedbackMarkers } from './aggFeedbackBlock';
import { useReplyRefine } from './useReplyRefine';
import { toEditableHtml } from './format';
import { COLORS } from './format';
import { SchedulingBox } from './SchedulingBox';
import { FeedbackNoteBox } from './FeedbackNoteBox';
import { PendingFeedbackBox } from './PendingFeedbackBox';
import { EvidenceBox } from './EvidenceBox';
import { AttachmentChips } from './AttachmentChips';
import { InboundCardActions } from './InboundCardActions';
import { LinkEditorModal } from './LinkEditorModal';
import { ROLE_UNKNOWN_COPY } from './roleUnknownCopy';
import { InterviewRescheduleButton } from '../InterviewRescheduleButton';

export function InboundCard({
  item,
  expanded,
  busy,
  onToggle,
  onSendReply,
  onMarkRead,
  onRemoveFromOverview,
  onLoadThread,
  onCancelInterview,
  cancelState,
  onLinkSaved,
  schedulingConnection,
  gmailToken,
  scheduleDisabledReason,
}: {
  item: InboundRequest;
  expanded: boolean;
  busy: boolean;
  onToggle: () => void;
  /** Queues the send behind the board's 10-second countdown; nothing goes out until it expires. */
  onSendReply: (bodyHtml: string) => void;
  /** `✓ Archive in Gmail` — read, labelled, and out of the inbox. See ClearActions. */
  onMarkRead: () => void;
  /** `Remove from overview` — same triage, same mark-as-read, but the mail stays in the inbox. */
  onRemoveFromOverview: () => void;
  /**
   * Fetches the WHOLE thread. Owned by the tab because the Gmail token and the API live there;
   * this component stays free of both.
   */
  onLoadThread: () => Promise<InboundMessage[]>;
  /** FINN-side cancel for the candidate's live booking; undefined = not offered on this card. */
  onCancelInterview?: () => void;
  cancelState?: 'idle' | 'pending' | 'cancelled' | 'already_cancelled';
  /** Re-reads the queue after the interview link was edited, so the card's diagnosis updates. */
  onLinkSaved?: () => void;
  /** For "Send later": the tab owns the Gmail token, this component stays free of the auth hook. */
  gmailToken?: string | null;
  /** Set when scheduling cannot work (no permanent Gmail connection) — the button says why. */
  scheduleDisabledReason?: string | null;
  /**
   * The Scheduling-tab connection for this candidate (RequestSchedulingConnection), owned by the
   * tab because it needs the whole board payload — this component stays free of it. A render
   * function rather than a node, because the connection needs ONE thing back from this card:
   * `proposeSlots`, which rewrites the reply editor with the ticked slots.
   */
  schedulingConnection?: (tools: ReplyTools | undefined) => ReactNode;
}) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [touched, setTouched] = useState(false);
  const [linkEditorOpen, setLinkEditorOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  /** Set once the candidate's reschedule link has been put into the reply. */
  const [rescheduleHinted, setRescheduleHinted] = useState(false);
  /**
   * Non-null once something was inserted programmatically. The editor is otherwise uncontrolled
   * (contentEditable), so this is the only way to push content in without stealing the recruiter's
   * own typing on every render.
   */
  const [editorHtml, setEditorHtml] = useState<string | null>(null);
  const draftHtml = item.decisionFeedback?.inDraft
    ? draftHtmlWithMarkedFeedback(item.draft || '', item.decisionFeedback.prose, toEditableHtml)
    : toEditableHtml(item.draft || '');

  /** One heuristic for both insertions below: reply in the language the draft already speaks. */
  const draftIsGerman =
    /^(hallo|hi|liebe)/i.test((item.draft || '').trim()) && /\bdu\b|\bdir\b|\bdein/i.test(item.draft || '');

  /** See replyProposal.tsx for the swap rules; this card only owns the editor state. */
  const originalPromise = originalPromiseOf(item.draft || '', draftIsGerman, item.category);
  const currentReply = () => editorRef.current?.innerHTML ?? draftHtml;
  /**
   * The proposal block as last WRITTEN — the mirror's proof a block is its own, not hand-edited.
   * Seeded with the block the PROXY wrote (item.proposal): the mirror owns that one the same way.
   */
  const lastWrittenProposal = useRef<string | null>(
    item.proposal?.kind === 'proposed' ? proposalIn(draftHtml) : null
  );
  // The card is keyed by thread and survives a refetch that re-picks the slots; while the editor is
  // still untouched it shows the NEW draft, so the provenance must follow it or every tick is ignored.
  useEffect(() => {
    if (editorHtml !== null) return;
    lastWrittenProposal.current = item.proposal?.kind === 'proposed' ? proposalIn(draftHtml) : null;
  }, [draftHtml, item.proposal?.kind, editorHtml]);
  const proposeSlots = (slots: GradedSlot[]) => {
    if (slots.length === 0) return;
    void logAnalyticsEvent('ta_requests_slots_proposed', { slot_count: slots.length, had_server_proposal: item.proposal?.kind === 'proposed' });
    const write = proposeInReply(currentReply(), slots, draftIsGerman);
    setEditorHtml(write.html);
    lastWrittenProposal.current = write.block;
    setTouched(true);
  };
  const syncProposalSlots = (slots: GradedSlot[]) => {
    const write = syncProposalInReply(
      currentReply(),
      slots,
      draftIsGerman,
      originalPromise,
      lastWrittenProposal.current
    );
    if (write === null) return;
    // The mirror IS the presented draft — it never flips Close to "Discard changes".
    setEditorHtml(write.html);
    lastWrittenProposal.current = write.block;
  };

  /** ✨ Rewrite — the hook owns reading the editor, writing it back, and undo. */
  const refine = useReplyRefine({
    item,
    german: draftIsGerman,
    editorRef,
    draftHtml,
    setEditorHtml,
    clearProposal: () => void (lastWrittenProposal.current = null),
    setTouched,
  });

  /**
   * "Let them reschedule": append the candidate's own link and the sentence that hands the next step
   * to them. FINN deliberately does not pick a new time — the standing rule is that the candidate
   * chooses, so this is a one-click way to say exactly that.
   *
   * Appends to what is IN THE EDITOR, not to the server draft, so a recruiter who has already edited
   * their reply does not lose those edits. The URL is inserted as an anchor because the reply is sent
   * as HTML and a bare string would be something to copy rather than click.
   */
  const insertRescheduleLink = () => {
    const url = item.scheduling?.bookingUrl;
    if (!url) return;
    const current = editorRef.current?.innerHTML ?? draftHtml;
    const sentence = draftIsGerman
      ? 'Deinen Termin kannst du hier selbst verschieben oder absagen:'
      : 'You can move or cancel your interview yourself here:';
    const anchor = `<a href="${url}">${url}</a>`;
    void logAnalyticsEvent('ta_requests_reschedule_link_inserted', { category: item.category, language: draftIsGerman ? 'de' : 'en' });
    setEditorHtml(`${current}<br><br>${sentence}<br>${anchor}`);
    setRescheduleHinted(true);
    setTouched(true);
  };

  /**
   * Clicking the card toggles it open and shut.
   *
   * Regions marked `data-no-toggle` are excluded rather than each child calling stopPropagation:
   * with the editor, two links and several buttons inside, a missed stopPropagation would collapse
   * the card mid-edit. Opting regions out keeps the safe default.
   */
  const onCardClick = (e: MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-no-toggle]')) return;
    onToggle();
  };

  // Keyboard equivalent, since the card is not a <button> (it contains its own controls).
  const onCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle();
    }
  };

  return (
    <div
      id={`ta-request-${item.threadId}`}
      className={cn(
        'cursor-pointer rounded-xl bg-white shadow-sm ring-1 transition-shadow',
        expanded ? 'ring-gray-300' : 'ring-gray-200 hover:shadow-md hover:ring-gray-300'
      )}
      onClick={onCardClick}
      onKeyDown={onCardKeyDown}
      tabIndex={0}
      // No aria-expanded here: assistive tech does not map it onto a generic div, and role="button"
      // would be wrong for an element containing its own links and buttons. The Reply control below
      // carries the accessible name and state.
      title={expanded ? 'Click to collapse' : 'Click to read the full email'}
    >
      {/* ── header: identity, Lever link, meta, status chips ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          {/* Gmail's own UNREAD on the candidate's latest message: a dot and a heavier name, the
              convention every mail client teaches, rather than another chip. */}
          {item.unread && (
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: COLORS.orangeText }}
              title="Unread — you have not opened this mail yet"
              aria-label="Unread"
            />
          )}
          <span className={cn('truncate text-sm text-gray-900', item.unread ? 'font-bold' : 'font-semibold')}>
            {item.candidateName}
          </span>
          {item.leverMatched && item.leverUrl ? (
            <a
              href={item.leverUrl}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-xs text-blue-600 hover:underline"
              title="Open in Lever"
              data-no-toggle
            >
              ↗ Lever
            </a>
          ) : (
            // A cold applicant has no Lever record yet. Saying so beats a dead link,
            // and it tells the recruiter why this card carries no role context.
            <span
              className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"
              title="Not in Lever — they emailed without applying, so there is no posting or stage"
            >
              not in Lever
            </span>
          )}
          {item.roleUnknownReason && (
            <span
              className="shrink-0 rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600"
              title={ROLE_UNKNOWN_COPY[item.roleUnknownReason].title}
            >
              {ROLE_UNKNOWN_COPY[item.roleUnknownReason].label}
            </span>
          )}
          <a
            href={item.gmailUrl}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 text-xs text-blue-600 hover:underline"
            title="Open the full thread in Gmail, including attachments"
            data-no-toggle
          >
            ✉ Gmail
          </a>
          {item.kickoffUrl && (
            <a
              href={item.kickoffUrl}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 text-xs text-blue-600 hover:underline"
              title="The role's kick-off sheet, including the job description"
              data-no-toggle
            >
              Kick-off
            </a>
          )}
          <span className="shrink-0 text-xs text-gray-400">
            {item.stageName ? `${item.stageName} · ` : ''}waiting {item.ageDays}d
          </span>
          <span className="shrink-0 text-xs text-gray-300" aria-hidden="true">
            {expanded ? '▾' : '▸'}
          </span>
        </div>
        <CardBadges item={item} />
      </div>

      {/* ── body: question (+ reply editor when open), actions to the right when closed ── */}
      <div className={cn(expanded ? 'block' : 'flex')}>
        <div className="min-w-0 flex-1 px-4 py-3">
          <p className={cn('mb-1 text-xs', item.unread ? 'font-semibold text-gray-600' : 'font-medium text-gray-400')}>
            {item.subject}
          </p>
          {/* Text, never HTML — see ThreadMessage. */}
          <p
            className={cn(
              'whitespace-pre-wrap text-sm leading-snug text-gray-700',
              expanded ? 'max-h-80 overflow-y-auto' : 'line-clamp-2'
            )}
          >
            {expanded ? item.questionFull : item.question}
          </p>
          {expanded && (item.attachments?.length ?? 0) > 0 && (
            <div className="mt-2">
              <AttachmentChips threadId={item.threadId} attachments={item.attachments!} gmailUrl={item.gmailUrl} />
            </div>
          )}
          {item.ageDays > 7 && (
            <p className="mt-2 text-xs font-semibold" style={{ color: COLORS.redText }}>
              {Math.round(item.ageDays)} days old
            </p>
          )}

          {expanded && <ThreadHistory item={item} onLoadThread={onLoadThread} />}

          {expanded && (
            <div className="mt-3 space-y-2" data-no-toggle>
              {/*
                First of the three: when a candidate asks what is happening, "the round has not
                been written up yet" is the fact that decides what the reply can honestly promise.
              */}
              <PendingFeedbackBox pending={item.pendingFeedback} />
              {/*
                Outside the draft branch on purpose: a scheduling request can arrive
                with no draft at all (a withheld template), and "Lena has no free
                slot in the window" is the most useful thing on the card in exactly
                that case. It is null-guarded, so non-scheduling items render nothing.
              */}
              <FeedbackNoteBox note={item.feedbackNote} />
              <SchedulingBox
                scheduling={item.scheduling}
                onCancelInterview={onCancelInterview}
                cancelState={cancelState}
                onRescheduleHint={insertRescheduleLink}
                rescheduleHinted={rescheduleHinted}
                onRescheduleForThem={
                  item.scheduling?.bookingRef ? () => { void logAnalyticsEvent('ta_requests_reschedule_opened', { category: item.category }); setRescheduleOpen(true); } : undefined
                }
                onEditLink={item.scheduling?.linkId ? () => { void logAnalyticsEvent('ta_requests_link_editor_opened', { category: item.category }); setLinkEditorOpen(true); } : undefined}
              />
              <ProposalBox proposal={item.proposal} />
              <RoleStatusLine status={item.roleStatus} />
              {/*
                MAIN's reschedule UI, reused verbatim: the Log page and this card must offer the same
                move, with the same override rules and the same candidate message. A second
                implementation was the drift risk — see the note in InterviewRescheduleButton.
              */}
              {item.scheduling?.bookingRef && rescheduleOpen && (
                <InterviewRescheduleButton
                  bookingRef={item.scheduling.bookingRef}
                  surface="requests"
                  candidateName={item.candidateName}
                  currentStart={null}
                  onRescheduled={onLinkSaved ?? (() => {})}
                />
              )}
              {item.scheduling?.linkId && linkEditorOpen && (
                <LinkEditorModal
                  linkId={item.scheduling.linkId}
                  open={linkEditorOpen}
                  onClose={() => setLinkEditorOpen(false)}
                  // Re-read the queue: the "no free slot" message is computed from the calendars, so
                  // a widened window has to show up on the card the recruiter is still looking at.
                  onSaved={onLinkSaved ?? (() => {})}
                />
              )}
              {schedulingConnection && (
                <ConnectionSlot
                  render={schedulingConnection}
                  // No draft → no editor is mounted, so no proposal tools: controls that write
                  // into a hidden editor would look dead and silently discard the recruiter's pick.
                  tools={item.draft ? { proposeSlots, syncProposalSlots } : undefined}
                />
              )}
              {item.draft ? (
                <>
                  {acknowledgesOnly(item) && item.flagReason && (
                    <div
                      className="rounded-lg p-3 text-sm"
                      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
                    >
                      <span className="font-semibold">Acknowledges only. </span>
                      {item.flagReason}
                    </div>
                  )}
                  {item.draftSource === 'template' && item.flagReason && (
                    // Some standard replies promise an action (sending an invite, forwarding
                    // documents) that the recruiter still has to perform — the reply must not
                    // go out with that promise unkept.
                    <div
                      className="rounded-lg p-3 text-sm"
                      style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
                    >
                      <span className="font-semibold">Before you send: </span>
                      {item.flagReason}
                    </div>
                  )}
                  {item.draftSource === 'model' && <EvidenceBox evidence={item.draftEvidence} />}
                  <p className="text-xs text-gray-500">
                    {item.draftSource === 'template'
                      ? 'Written for this message, following FINN’s approved reply for this kind of request. '
                      : item.draftSource === 'holding' && item.proposal?.kind === 'proposed'
                        ? 'Written for this message, with the free panel slots from your Scheduling tab in place of the usual “I will come back with times”. '
                        : item.draftSource === 'holding'
                          ? 'Written for this message so nobody waits. It deliberately does not answer the question. '
                        : // A feedback reply is written FROM the interviewers' notes (which are
                          // never sent), so it must not claim the posting as its source.
                          item.draftSource === 'feedback'
                          ? 'Written from the interviewers’ notes, through the same checks as a rejection email. Read it before sending. '
                          : item.draftEvidence?.length
                            ? 'Drafted from the sources named above, and only from them. '
                            : 'Drafted from the public job posting and this candidate’s Lever profile. '}
                    Sending replies on the same Gmail thread, from your own account —{' '}
                    <span className="font-medium">you get 10 seconds to stop it</span>.
                  </p>
                  <DecisionFeedbackControl item={item} editorRef={editorRef} german={draftIsGerman} onChanged={() => setTouched(true)} />
                  <EmailBodyEditor
                    ref={editorRef}
                    html={editorHtml ?? draftHtml}
                    disabled={busy}
                    onChanged={() => setTouched(true)}
                    refine={refine}
                  />
                </>
              ) : item.draftSource === 'no_reply' ? (
                <div className="rounded-lg bg-gray-100 p-3 text-sm text-gray-600">
                  <span className="font-semibold">No reply needed. </span>
                  {item.flagReason} If that reads wrong, reply as usual.
                </div>
              ) : (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{ backgroundColor: COLORS.orangeBg, color: COLORS.orangeText }}
                >
                  <span className="font-semibold">Needs you. </span>
                  {item.flagReason}
                </div>
              )}
            </div>
          )}
        </div>

        {!expanded && (
          // data-no-toggle: these buttons run their own actions; without the opt-out their click
          // also bubbles to the card's toggle, and Reply's expand + the card's toggle cancel out.
          // Both labels are centred and fit one line in this column. A wrapped, left-aligned
          // label sat ragged beside the centred Reply; `whitespace-normal` stays on them so a
          // narrower column wraps rather than overflowing.
          <div
            className="flex w-48 shrink-0 flex-col justify-center gap-2 border-l border-gray-100 px-3 py-3"
            data-no-toggle
          >
            <Button
              size="sm"
              className="w-full whitespace-normal text-center leading-tight"
              onClick={onToggle}
              aria-expanded={expanded}
              aria-controls={`ta-request-${item.threadId}`}
            >
              Reply
            </Button>
            <ClearActions
              busy={busy}
              onMarkRead={onMarkRead}
              onRemoveFromOverview={onRemoveFromOverview}
              stacked
            />
          </div>
        )}
      </div>

      {expanded && (
        <InboundCardActions
          item={item}
          busy={busy}
          touched={touched}
          onToggle={onToggle}
          onSendReply={onSendReply}
          onMarkRead={onMarkRead}
          onRemoveFromOverview={onRemoveFromOverview}
          readEditor={() =>
            // `? :` on the ref, not `||` on the value: an emptied editor yields '', and falling back
            // would send the generated draft the recruiter had just deleted. stripFeedbackMarkers:
            // the data-agg-feedback span is an internal affordance and must not ship (same rule as
            // the Archive modal, which strips before reading).
            stripFeedbackMarkers(editorRef.current ? editorRef.current.innerHTML : draftHtml)
          }
          gmailToken={gmailToken ?? null}
          scheduleDisabledReason={scheduleDisabledReason ?? null}
        />
      )}
    </div>
  );
}
